import { useEffect, useRef, useState } from "react";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";

const parseRate = (v) => {
  const n = parseFloat(String(v).replace(",", "."));
  return n >= 3 && n <= 10 ? Math.round(n * 10000) / 10000 : null;
};

/**
 * Dual currency input (LEI / EUR). Emits EUR via onChange and
 * { original_amount, original_currency, rate_at_entry, rate_locked } via onMeta.
 * Admins get a "Curs folosit" field to lock a custom rate for this transaction.
 */
export default function DualMoneyInput({ value, onChange, onMeta, autoFocus, testid }) {
  const { rate: activeRate } = useCurrency();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [eurStr, setEurStr] = useState(value ? String(value) : "");
  const [leiStr, setLeiStr] = useState("");
  const [rateStr, setRateStr] = useState("");
  const touchedRef = useRef(null);
  const lastRef = useRef({ amount: null, currency: null });
  const selfClearRef = useRef(false);

  const custom = parseRate(rateStr);
  const locked = isAdmin && custom != null && custom !== activeRate;
  const rate = locked ? custom : activeRate;

  useEffect(() => { if (!rateStr && activeRate > 1) setRateStr(String(activeRate)); }, [activeRate, rateStr]);

  useEffect(() => {
    if ((value === "" || value == null) && selfClearRef.current) {
      selfClearRef.current = false;
    } else if ((value === "" || value == null) && touchedRef.current) {
      touchedRef.current = null;
      lastRef.current = { amount: null, currency: null };
      setRateStr(activeRate > 1 ? String(activeRate) : "");
    }
    if (touchedRef.current) return;
    if (value === "" || value == null) {
      setEurStr("");
      setLeiStr("");
      return;
    }
    const eurNum = Number(value);
    setEurStr(String(eurNum));
    setLeiStr(rate ? String(Math.round(eurNum * rate)) : "");
  }, [value, rate]);

  function emit(amount, currency, r = rate, isLocked = locked) {
    lastRef.current = { amount, currency };
    if (!onMeta) return;
    if (amount === "" || amount == null || isNaN(Number(amount))) {
      onMeta({ original_amount: null, original_currency: null, rate_at_entry: null, rate_locked: false });
      return;
    }
    onMeta({ original_amount: Number(amount), original_currency: currency, rate_at_entry: r || null, rate_locked: isLocked });
  }

  function recalc(fromLei, v, r) {
    selfClearRef.current = false;
    if (fromLei) {
      const eur = r > 0 ? Math.round((Number(v) / r) * 100) / 100 : 0;
      setEurStr(String(eur));
      onChange?.(eur);
    } else {
      setLeiStr(r > 0 ? String(Math.round(Number(v) * r)) : "");
      onChange?.(Number(v));
    }
  }

  function changeLei(v) {
    touchedRef.current = "lei";
    setLeiStr(v);
    if (v === "" || isNaN(Number(v))) {
      setEurStr("");
      selfClearRef.current = true;
      onChange?.("");
      emit(null, null);
      return;
    }
    recalc(true, v, rate);
    emit(Number(v), "RON");
  }

  function changeEur(v) {
    touchedRef.current = "eur";
    setEurStr(v);
    if (v === "" || isNaN(Number(v))) {
      setLeiStr("");
      selfClearRef.current = v === "";
      onChange?.(v === "" ? "" : 0);
      emit(null, null);
      return;
    }
    recalc(false, v, rate);
    emit(Number(v), "EUR");
  }

  function changeRate(v) {
    setRateStr(v);
    const c = parseRate(v);
    const isLocked = c != null && c !== activeRate;
    const r = isLocked ? c : activeRate;
    if (lastRef.current.amount == null && value !== "" && value != null && !isNaN(Number(value))) {
      lastRef.current = { amount: Number(value), currency: "EUR" };
    }
    const { amount, currency } = lastRef.current;
    if (amount == null) return;
    recalc(currency === "RON", amount, r);
    emit(amount, currency, r, isLocked);
  }

  const inputCls = "w-full border border-slate-200 rounded-lg pl-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]";
  return (
    <div className="space-y-1.5">
      <div className="grid grid-cols-2 gap-2">
        <div className="relative">
          <input data-testid={`${testid}-lei`} type="number" inputMode="decimal" autoFocus={autoFocus} placeholder="Sumă lei"
            value={leiStr} onChange={(e) => changeLei(e.target.value)} className={`${inputCls} pr-9`} />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">lei</span>
        </div>
        <div className="relative">
          <input data-testid={`${testid}-eur`} type="number" inputMode="decimal" placeholder="Sumă €"
            value={eurStr} onChange={(e) => changeEur(e.target.value)} className={`${inputCls} pr-7 font-medium`} />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">€</span>
        </div>
      </div>
      {isAdmin && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500 whitespace-nowrap">
          <span>Curs folosit:</span>
          <input data-testid={`${testid}-rate`} inputMode="decimal" value={rateStr} onChange={(e) => changeRate(e.target.value)}
            className={`w-20 border rounded-md px-1.5 py-0.5 tabular focus:outline-none focus:ring-2 focus:ring-[#0047FF] ${rateStr && custom == null ? "border-red-300" : "border-slate-200"}`} />
          <span>lei / €</span>
          {locked && <span data-testid={`${testid}-rate-custom`} className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">curs propriu</span>}
          {rateStr && custom == null && <span className="text-red-600">între 3 și 10</span>}
        </div>
      )}
    </div>
  );
}
